import React from "react";
import { MemberSheetDataDataType, MembersSheetProps } from "./SheetContent";
import { MemberCard } from "./MemberCard";
import { Button, Card } from "@pushpress/pushpress-ui";
import {
  AtRiskMembers,
  ExpiringMembers,
  NewLeads,
  NewMemberships,
  OpenInvoices,
  SubscriptionCancelRequest,
} from "../../../apis/metricsApi";
import { ChevronRight, Info } from "lucide-react";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { cn } from "../../../utils/css";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";
import { unixTimestampToDateString } from "./dateHeadingHelpers";
import { SheetEmptyState } from "./SheetEmptyState";

const HelpText = {
  openInvoices: "Follow up with these members about their open invoices.",
  atRiskMembers:
    "Members who have not checked in in for a while and are at risk of cancelling.",
  expiringMembers:
    "Follow up with these members to see if they'd like to renew their plan.",
  newLeads:
    "Reach out to these leads to see if they're interested in joining your community.",
  newMemberships: "Members who have recently purchased a new plan.",
  cancelRequests:
    "Members who have requested cancellation for a recurring membership.",
};

export const MembersSheetContent: React.FC<MembersSheetProps> = ({
  data,
  title,
  reportUrl,
}) => {
  const { trackEvent } = useAnalytics();
  const legacyNavigate = useLegacyNavigate();
  const showEmptyState = data.data && data.data.length === 0;

  const generateSubheading = (member: MemberSheetDataDataType) => {
    switch (data.type) {
      case "openInvoices": {
        const openInvoices = member as OpenInvoices;
        return `$${openInvoices.total}`;
      }
      case "expiringMembers": {
        const expiringMember = member as ExpiringMembers;
        return `${expiringMember.planName}`;
      }
      case "atRiskMembers": {
        const atRiskMember = member as AtRiskMembers;
        return `Last checkin: ${unixTimestampToDateString(
          atRiskMember.lastCheckin,
        )}`;
      }
      case "newLeads": {
        const newLeads = member as NewLeads;
        return newLeads?.referralSource || "";
      }
      case "newMemberships": {
        const newMemberships = member as NewMemberships;
        return `${newMemberships.planName}`;
      }
      default:
        return "";
    }
  };

  const sentiments = [
    { value: "openInvoices", sentiment: "negative" },
    { value: "atRiskMembers", sentiment: "negative" },
    { value: "expiringMembers", sentiment: "negative" },
    { value: "newLeads", sentiment: "positive" },
    { value: "newMemberships", sentiment: "positive" },
    { value: "cancelRequests", sentiment: "negative" },
  ];
  const sentiment = sentiments.find((s) => s.value === data.type).sentiment;

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      <div
        id="sheet-container"
        className="tw-flex tw-flex-col tw-max-h-[80vh] sm:tw-max-h-screen tw-flex-1"
      >
        {/* Inner container */}
        <div
          id="sheet-inner-container"
          className="tw-p-4 tw-space-y-4 tw-flex-1 tw-overflow-auto"
        >
          {/* Banner */}
          <div id="sheet-banner">
            {!showEmptyState && HelpText[data.type] && (
              <Card
                className={cn(
                  "tw-p-2 tw-outline-2",
                  sentiment === "negative"
                    ? "tw-bg-amber-100/30 tw-outline-amber-100 tw-border-amber-300"
                    : "tw-bg-green-50 tw-outline-green-100 tw-border-green-300",
                )}
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-rounded-full tw-px-8 sm:tw-px-0">
                    <Info
                      className={cn(
                        "tw-w-6 tw-h-6",
                        sentiment === "negative"
                          ? "tw-text-amber-600 tw-border-amber-200"
                          : " tw-text-green-600 tw-border-green-200",
                      )}
                    />
                  </div>
                  <div>
                    <p
                      className={cn(
                        "tw-text-sm tw-mb-1",
                        sentiment === "negative"
                          ? "tw-text-amber-600"
                          : "tw-text-green-600",
                      )}
                    >
                      {HelpText[data.type]}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Empty State */}
          {showEmptyState && <SheetEmptyState type={data.type} />}

          {/* Items */}
          <div id="sheet-items" className="tw-space-y-4 tw-flex-1 tw-pb-12">
            {!showEmptyState &&
              data.data.length > 0 &&
              data.data.map((member: MemberSheetDataDataType, index) => (
                <MemberCard
                  key={`${member.userUuid}-${index}`}
                  profilePic={member.userImageUrl}
                  firstName={member.firstName}
                  lastName={member.lastName}
                  cardSubheading={generateSubheading(member)}
                  cancelRequest={
                    data.type === "cancelRequests"
                      ? (member as SubscriptionCancelRequest)
                      : undefined
                  }
                  onViewProfile={() => {
                    if ("invoiceUuid" in member) {
                      trackEvent("Member Profile Card Clicked", {
                        type: data.type,
                        card_type: "open_invoice_member_card",
                      });
                      legacyNavigate(`/invoices/invoice/${member.invoiceUuid}`);
                    } else {
                      trackEvent("Member Profile Card Clicked", {
                        type: data.type,
                        card_type: "standard_member_card",
                      });
                      legacyNavigate(`/members/view/${member.userUuid}`);
                    }
                  }}
                />
              ))}
          </div>
        </div>

        {/* Footer */}
        <div
          id="sheet-footer"
          className="tw-bg-white tw-p-4 tw-border-t tw-sticky tw-bottom-0"
        >
          {reportUrl && (
            <div className="tw-flex tw-justify-center tw-w-full">
              <Button
                variant="outline"
                onClick={() => legacyNavigate(reportUrl)}
                className="tw-w-full tw-shadow-none"
              >
                <div>View all {title?.toLowerCase()}</div>
                <div>
                  <ChevronRight className="tw-w-4 tw-h-4" />
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
